// src/components/discovery/CreateProblemDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Badge } from "components/ui/badge";
import { X, Plus } from "lucide-react";
import { okrsService } from "../../services/database";
import ConfidencePicker, {
  CONFIDENCE_DIMENSIONS,
  DEFAULT_CONFIDENCE,
} from "./ConfidencePicker";

const EVIDENCE_TYPES = [
  "interview",
  "survey",
  "usability",
  "support_ticket",
  "heatmap",
  "tech_spike",
  "architecture",
  "feasibility_doc",
  "repo_link",
  "ab_test",
  "analytics",
  "cohort",
  "pricing",
  "finance",
  "link",
];

export default function CreateProblemDialog({ open, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    severity: "medium",
    target_segment: "",
    desirability_score: DEFAULT_CONFIDENCE,
    feasibility_score: DEFAULT_CONFIDENCE,
    viability_score: DEFAULT_CONFIDENCE,
    status: "discovery",
    tags: [],
    // NEW
    okr_ref: "",
    time_horizon: "", // "Now" | "Next" | "Later"
  });

  const [newTag, setNewTag] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [okrs, setOkrs] = useState([]);

  // Load OKRs when dialog opens
  useEffect(() => {
    if (!open) return;

    const loadOkrs = async () => {
      try {
        const okrData = await okrsService.list("-updated_at", 100);
        setOkrs(okrData);
      } catch (error) {
        console.error("Failed to load OKRs:", error);
        setOkrs([]);
      }
    };

    loadOkrs();
  }, [open]);

  // NEW: evidence editor state
  const [evidence, setEvidence] = useState([]);
  const addEvidence = () =>
    setEvidence((prev) => [
      ...prev,
      { type: "interview", url: "", note: "", dateISO: "" },
    ]);
  const updateEvidence = (idx, patch) =>
    setEvidence((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  const removeEvidence = (idx) =>
    setEvidence((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        // trim evidence urls/notes
        evidence: evidence
          .map((ev) => ({
            ...ev,
            url: (ev.url || "").trim(),
            note: (ev.note || "").trim(),
            dateISO: ev.dateISO || "",
          }))
          .filter((ev) => ev.url), // keep only rows with URL
      };
      await Promise.resolve(onSubmit?.(payload));

      // reset
      setFormData({
        title: "",
        description: "",
        severity: "medium",
        target_segment: "",
        desirability_score: DEFAULT_CONFIDENCE,
        feasibility_score: DEFAULT_CONFIDENCE,
        viability_score: DEFAULT_CONFIDENCE,
        status: "discovery",
        tags: [],
        okr_ref: "",
        time_horizon: "",
      });
      setEvidence([]);
      setNewTag("");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error creating problem:", err);
    }
    setIsSubmitting(false);
  };

  const addTag = () => {
    const t = newTag.trim();
    if (t && !formData.tags.includes(t)) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, t] }));
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tagToRemove),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <div
          style={{
            maxWidth: 780,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <DialogHeader>
            <DialogTitle
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "var(--fg)",
                marginBottom: 8,
              }}
            >
              Create New Problem Card
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 16 }}>
            {/* Basic Info */}
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <Label htmlFor="title">Problem Title *</Label>
                <Input
                  id="title"
                  required
                  placeholder="Describe the core problem in one sentence"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, title: e.target.value }))
                  }
                  style={{ marginTop: 6 }}
                />
              </div>

              <div>
                <Label htmlFor="description">Problem Description *</Label>
                <Textarea
                  id="description"
                  required
                  placeholder="Provide detailed context about this problem..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, description: e.target.value }))
                  }
                  style={{ marginTop: 6, minHeight: 96 }}
                />
              </div>
            </div>

            {/* Classification */}
            <div
              style={{
                display: "grid",
                gap: 12,
                gridTemplateColumns: "1fr 1fr",
              }}
            >
              <div>
                <Label>Severity Level</Label>
                <div style={{ marginTop: 6 }}>
                  <Select
                    value={formData.severity}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, severity: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="target_segment">Customer Segment</Label>
                <Input
                  id="target_segment"
                  placeholder="e.g., Enterprise users, SMB owners"
                  value={formData.target_segment}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      target_segment: e.target.value,
                    }))
                  }
                  style={{ marginTop: 6 }}
                />
              </div>
            </div>

            {/* Confidence Scores */}
            <div>
              <Label style={{ display: "block", marginBottom: 2 }}>
                Initial Confidence
              </Label>
              <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--fg-muted)" }}>
                How strong is the evidence today?
              </p>
              <div
                style={{
                  display: "grid",
                  gap: 14,
                  background: "var(--bg-subtle)",
                  padding: 12,
                  borderRadius: 12,
                }}
              >
                {CONFIDENCE_DIMENSIONS.map((dim) => (
                  <ConfidencePicker
                    key={dim.id}
                    dimension={dim}
                    value={formData[`${dim.id}_score`]}
                    onChange={(n) =>
                      setFormData((p) => ({ ...p, [`${dim.id}_score`]: n }))
                    }
                  />
                ))}
              </div>
            </div>

            {/* OKR + Time Horizon (NEW) */}
            <div
              style={{
                display: "grid",
                gap: 12,
                gridTemplateColumns: "1fr 1fr",
              }}
            >
              <div>
                <Label>Linked OKR (optional)</Label>
                <div style={{ marginTop: 6 }}>
                  <Select
                    value={formData.okr_ref}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, okr_ref: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an OKR..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {okrs.map((okr) => (
                        <SelectItem key={okr.id} value={String(okr.id)}>
                          {okr.title} ({okr.quarter} {okr.year})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Time Horizon</Label>
                <div style={{ marginTop: 6 }}>
                  <Select
                    value={formData.time_horizon}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, time_horizon: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select horizon" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Now">Now</SelectItem>
                      <SelectItem value="Next">Next</SelectItem>
                      <SelectItem value="Later">Later</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div style={{ marginTop: 6, display: "grid", gap: 8 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    value={newTag}
                    placeholder="Add a tag..."
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button type="button" onClick={addTag}>
                    <Plus size={16} />
                  </Button>
                </div>

                {formData.tags.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {formData.tags.map((tag) => (
                      <Badge
                        key={tag}
                        className="text-xs"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: "var(--bg-subtle)",
                          color: "var(--fg-secondary)",
                          border: 0,
                        }}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          title="Remove"
                          style={{
                            border: 0,
                            background: "transparent",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <X size={12} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Evidence (NEW) */}
            <div
              style={{
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h3 style={{ margin: 0, fontWeight: 600 }}>Evidence</h3>
                <Button type="button" variant="outline" onClick={addEvidence}>
                  + Add Evidence
                </Button>
              </div>

              {evidence.length === 0 && (
                <p style={{ marginTop: 8, color: "var(--fg-muted)", fontSize: 14 }}>
                  Attach links to interviews, surveys, usability sessions,
                  analytics, A/B tests, tech spikes, etc.
                </p>
              )}

              <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                {evidence.map((ev, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "grid",
                      gap: 8,
                      gridTemplateColumns: "1.2fr 2.2fr 1.6fr 1fr auto",
                      alignItems: "center",
                    }}
                  >
                    {/* Type */}
                    <div>
                      <Label className="text-xs">Type</Label>
                      <div style={{ marginTop: 6 }}>
                        <Select
                          value={ev.type}
                          onValueChange={(v) =>
                            updateEvidence(idx, { type: v })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {EVIDENCE_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* URL */}
                    <div>
                      <Label className="text-xs">URL</Label>
                      <Input
                        placeholder="https://..."
                        required
                        value={ev.url}
                        onChange={(e) =>
                          updateEvidence(idx, { url: e.target.value })
                        }
                        style={{ marginTop: 6 }}
                      />
                    </div>

                    {/* Note */}
                    <div>
                      <Label className="text-xs">Note</Label>
                      <Input
                        placeholder="short context"
                        value={ev.note}
                        onChange={(e) =>
                          updateEvidence(idx, { note: e.target.value })
                        }
                        style={{ marginTop: 6 }}
                      />
                    </div>

                    {/* Date */}
                    <div>
                      <Label className="text-xs">Date</Label>
                      <Input
                        type="date"
                        value={ev.dateISO}
                        onChange={(e) =>
                          updateEvidence(idx, { dateISO: e.target.value })
                        }
                        style={{ marginTop: 6 }}
                      />
                    </div>

                    {/* Remove */}
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeEvidence(idx)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                paddingTop: 12,
                borderTop: "1px solid var(--border)",
              }}
            >
              <Button type="button" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: "var(--accent-surface)",
                  border: "none",
                }}
              >
                {isSubmitting ? "Creating..." : "Create Problem Card"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
